import { defineStore } from 'pinia'
import { actions, getters, state } from './ext/items.js'
import axios from 'axios'
import { useFundraiserPageStore } from '@/stores/fundraiserPage.js'

export const useFundraiserStore = (id = 'default') => {
  return defineStore( {
    id: `fundraisers_${id}`,
    state: () => ({
      ...state(),
      api: '/api/v1/fundraisers'
    }),
    getters: {
      getItemById (state) {
        return (itemId) => state.items.find(item => item.id === itemId)
      },
      getItems (state) {
        return state.items
      },
      getItemsByOrganizationId (state) {
        return (organizationId) => state.items.filter(item => item.beneficiary_id === organizationId && item.beneficiary_type === 'App\\Models\\Organization')
      },
      getItemsFeatured (state) {
        return state.items.filter(item => item.featured)
      }
    },
    actions: {
      ...actions,
      async fetchItemsByOrganizationId (organizationId, params) {
        return await this.fetchItems({
          beneficiary_id: organizationId,
          beneficiary_type: 'App\\Models\\Organization',
          ...params
        })
      },
      async fetchFeaturedItems (params) {
        const { data } = await axios.get(this.api, {
          params: {
            featured: true,
            not_paginated: true,
            ...params
          }
        })
        this.items = data.data
      },
      async fundraiserDetailsPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/details`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserCommunicationPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/communication`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserBackgroundPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/background-image`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserBannerPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/banner-image`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserSlideShowPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/slideshow`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserLogoPatch (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/logo`, payload)
        this.itemPatchState(data)
        return data
      },
      async fundraiserValidateSlug (payload) {
        const { data } = await axios.post(`${this.api}/${payload.id}/validate-slug`, payload)
        return data
      },
      async fundraiserPublish (fundraiserId) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/publish`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserUnPublish (fundraiserId) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/unpublish`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserMarkAsFeatured (fundraiserId) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/mark-as-featured`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserMarkAsNotFeatured (fundraiserId) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/mark-as-not-featured`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserSettingsPatch (fundraiserId, settings) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/settings`, settings)
        this.itemPatchState(data)
        return data
      },
      async fundraiserJoin (fundraiserId, payload) {
        const { data } = await axios.post(`${this.api}/${fundraiserId}/join`, {
          ...payload,
          fundraiser_id: fundraiserId
        })
        return data
      },
      async fetchDashboardReport (fundraiserId, params) {
        const { data } = await axios.get(`/api/v1/fundraisers/${fundraiserId}/dashboard-report`, {
          params
        })
        return data
      },
      async saveFundraiserCustomFields (fundraiserId, params) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/custom-fields`, params)
        return data
      },
      async createEmailCampaignDripFeed (fundraiserId) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/create-email-campaign-drip-feed`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserDisableEmailCampaignDripFeed (fundraiserId) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/disable-email-campaign-drip-feed`)
        this.itemPatchState(data)
        return data
      },
      async fundraiserGroupSettingsOverrideDescriptions (fundraiserId, defaultLongDesc) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/group-settings-override-descriptions`, {
          default_long_desc: defaultLongDesc
        })
        this.itemPatchState(data)
        return data
      },
      async fundraiserGroupSettingsOverrideGoals (fundraiserId, defaultGoal) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/group-settings-override-goal`, {
          default_goal: defaultGoal
        })
        this.itemPatchState(data)
        return data
      },
      async rebuildCache(fundraiserId) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/rebuild-cache`)
        this.itemPatchState(data)
        return data
      },
      async saveSlug(fundraiserId, slug) {
        const { data } = await axios.post(`/api/v1/fundraisers/${fundraiserId}/slug`,{slug})
        this.itemPatchState(data)
        return data
      },
      async fetchDonorEmailsById(fundraiserId) {
        const { data } = await axios.get(`/api/v1/fundraisers/${fundraiserId}/donor-emails`)
        return data
      },
      async fetchSubscriberEmailsById(fundraiserId) {
        const { data } = await axios.get(`/api/v1/fundraisers/${fundraiserId}/subscriber-emails`)
        return data
      },
      async fetchFundraiserWebconnexIntegrations(fundraiserId) {
        const { data } =  await axios.get(`/api/v1/fundraisers/${fundraiserId}/webconnex-integrations`)
        return data
      },
      async createWebConnexIntegration(fundraiserId, payload) {
        const { data } =  await axios.post(`/api/v1/fundraisers/${fundraiserId}/webconnex-integrations`, payload)
        return data
      }
    }
  })()
}

